<script setup>
import { computed } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import { tourCategories, experiences } from '@/data/content.js'

defineOptions({ layout: PublicLayout })

const props = defineProps({
  tours:     { type: Array,  default: () => [] },
  filters:   { type: Object, default: () => ({}) },
  base_path: { type: String, default: '/tours' },
})

const categoryKeys = Object.keys(tourCategories).filter((k) => k !== 'other')
const hasLiveTours = computed(() => props.tours.length > 0)

function durationLabel(mins) {
  if (!mins) return ''
  const h = Math.round(mins / 60)
  return h >= 8 ? 'Full day' : h <= 1 ? `${mins} min` : `${h}h`
}
function applyFilter(value) {
  router.get(props.base_path, { ...props.filters, category: value }, { preserveScroll: true })
}
</script>

<template>
  <Head>
    <title>Experiences · Kenya2Go</title>
    <meta name="description" content="Safaris, the Swahili coast and cultural experiences across Kenya — request to book with local operators.">
  </Head>

  <div class="kp">
    <header class="kp-hero">
      <div class="kp-hero__inner">
        <p class="kp-eyebrow">Experiences</p>
        <h1 class="kp-hero__title">Experiences, not packages</h1>
        <p class="kp-hero__lede">Every experience is run by a vetted local operator. Find one you love and send a request — a real person confirms the details.</p>
      </div>
    </header>

    <section class="kp-section">
      <div class="filters">
        <button :class="['chip', !filters.category && 'is-active']" @click="applyFilter(null)">All</button>
        <button v-for="key in categoryKeys" :key="key" :class="['chip', filters.category === key && 'is-active']" @click="applyFilter(key)">
          {{ tourCategories[key] }}
        </button>
      </div>

      <!-- Live catalog -->
      <ul v-if="hasLiveTours" class="tgrid">
        <li v-for="tr in tours" :key="tr.id" class="kp-card tcard">
          <a :href="`${base_path}/${tr.id}`" class="tcard__link">
            <div class="tcard__photo">
              <img v-if="tr.photo_url" :src="tr.photo_url" :alt="tr.title">
              <CatalogPlaceholder v-else kind="tour" :category="tr.category" />
            </div>
            <div class="tcard__body">
              <h3 class="tcard__title">{{ tr.title }}</h3>
              <p v-if="tr.summary" class="tcard__summary">{{ tr.summary }}</p>
              <p class="tcard__meta">
                <span>{{ tourCategories[tr.category] || tr.category }}</span>
                <span class="tcard__price">{{ tr.price_adult }}</span>
              </p>
            </div>
          </a>
        </li>
      </ul>

      <!-- Pre-launch: show curated experiences as a taste -->
      <template v-else>
        <p class="soon">Our first experiences are launching soon — here’s a taste of what’s coming.</p>
        <div class="tgrid">
          <article v-for="x in experiences" :key="x.title" class="kp-card tcard" :style="{ '--h': x.hue }">
            <div class="kp-art tcard__art"><span class="tcard__kind">{{ x.kind }}</span></div>
            <div class="tcard__body">
              <h3 class="tcard__title">{{ x.title }}</h3>
              <p class="tcard__summary">{{ x.blurb }}</p>
              <p class="tcard__meta">
                <span>{{ x.days }}</span>
                <span class="tcard__price">from ${{ x.from }}</span>
              </p>
            </div>
          </article>
        </div>
        <div class="soon-cta">
          <a href="/contact" class="kp-btn kp-btn--solid">Ask about an experience</a>
        </div>
      </template>
    </section>
  </div>
</template>

<style scoped>
.filters { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 2rem; }
.chip { padding: 0.5rem 1rem; border: 1px solid var(--line); background: var(--surface); border-radius: 999px; font-size: 0.88rem; color: var(--inkSoft); cursor: pointer; font-weight: 500; transition: all .12s ease; }
.chip:hover { border-color: var(--accent); }
.chip.is-active { background: var(--accent); color: var(--accentInk); border-color: var(--accent); }

.tgrid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem; }
.tcard { overflow: hidden; }
.tcard__link { display: block; color: inherit; text-decoration: none; }
.tcard__photo { aspect-ratio: 4 / 3; background: var(--surfaceAlt); display: grid; place-items: center; overflow: hidden; }
.tcard__photo img { width: 100%; height: 100%; object-fit: cover; }
.tcard__art { aspect-ratio: 4 / 3; display: flex; align-items: flex-start; padding: 0.8rem; }
.tcard__kind { position: relative; z-index: 1; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: oklch(0.99 0.02 85); background: oklch(0.2 0.04 40 / 0.3); padding: 0.25rem 0.6rem; border-radius: 999px; }
.tcard__body { padding: 1rem 1.1rem 1.2rem; }
.tcard__title { font-family: var(--display); font-weight: 500; font-size: 1.25rem; line-height: 1.1; margin: 0 0 0.4rem; color: var(--ink); }
.tcard__summary { font-size: 0.9rem; line-height: 1.5; color: var(--inkSoft); margin: 0 0 0.7rem; }
.tcard__meta { display: flex; align-items: baseline; justify-content: space-between; font-size: 0.85rem; color: var(--muted); margin: 0; }
.tcard__price { font-weight: 600; color: var(--accent); }

.soon { text-align: center; color: var(--inkSoft); margin: 0 0 2rem; font-size: 1rem; }
.soon-cta { text-align: center; margin-top: 2.5rem; }

@media (max-width: 860px) { .tgrid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px) { .tgrid { grid-template-columns: 1fr; } }
</style>
